/**
 * P13 — "Squishy Making Inspiration" / Watch & Make (PDP).
 *
 * Video state only. Navigation, drag, inertia, snapping and keyboard handling
 * stay in `RyoxzelRail`; the modal shell, focus trap, Escape and scroll lock stay
 * in Horizon's `dialog-component`; the video itself is Horizon's `video` snippet
 * inside a `<deferred-media>`. This file owns exactly two things: which lightbox
 * slide is active, and when that slide's media mounts and is torn down.
 *
 * That is the same split the UI Library records for S10 ("UGC JavaScript owns
 * video state only; navigation motion is shared"), applied to the PDP rail. No
 * carousel, drag controller, modal or arrow system is defined here.
 *
 * Loaded only by sections/ryoxzel-pdp-inspiration.liquid.
 */

const shelves = new WeakMap();

class RyoxzelInspirationShelf {
  constructor(section) {
    this.section = section;
    this.viewport = section.querySelector('[data-ryoxzel-rail-viewport]');
    this.triggers = [...section.querySelectorAll('[data-ryoxzel-inspiration-trigger]')];
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();

    // The lightbox is a sibling of the section element, inside the same
    // `#shopify-section-…` wrapper, so it is resolved by id rather than by
    // document order.
    const lightboxId = section.dataset.sectionId
      ? `ryoxzel-inspiration-lightbox-${section.dataset.sectionId}`
      : null;
    this.lightbox = lightboxId ? document.getElementById(lightboxId) : null;
    this.dialog = this.lightbox?.querySelector('dialog') ?? null;
    this.lightboxViewport = this.lightbox?.querySelector('[data-ryoxzel-inspiration-lightbox-viewport]') ?? null;
    this.slides = this.lightbox ? [...this.lightbox.querySelectorAll('[data-ryoxzel-inspiration-slide]')] : [];
    this.status = this.lightbox?.querySelector('[data-ryoxzel-inspiration-status]') ?? null;
    this.mountedIndex = null;
    this.opener = null;
    this.frame = 0;
    this.mountTimer = 0;

    if (!this.viewport || this.triggers.length === 0) return;

    const { signal } = this.abortController;
    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.open(index, trigger), { signal });
    });
    this.dialog?.addEventListener('close', () => this.unmount(), { signal });
    this.lightbox?.addEventListener('dialog:close', () => this.opener?.focus(), { signal });
    this.lightboxViewport?.addEventListener('scroll', () => this.scheduleActiveSlide(), { passive: true, signal });
    window.addEventListener('resize', () => this.scheduleActiveSlide(), { passive: true, signal });
  }

  open(index, trigger) {
    if (!this.lightbox || this.slides.length === 0) return;
    this.opener = trigger;
    this.lightbox.showDialog();
    // Two frames so the dialog is laid out before the rail is centred on a slide.
    requestAnimationFrame(() => requestAnimationFrame(() => this.goToSlide(index, 'auto')));
  }

  goToSlide(index, behavior) {
    const total = this.slides.length;
    if (!total) return;
    const target = (index + total) % total;
    this.activate(target);
    this.slides[target].scrollIntoView({
      behavior: behavior ?? (this.reducedMotion.matches ? 'auto' : 'smooth'),
      inline: 'center',
      block: 'nearest',
    });
  }

  scheduleActiveSlide() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      // Never mount media while the dialog is closed: a viewport resize would
      // otherwise start a video behind a hidden dialog.
      if (!this.lightboxViewport || !this.dialog?.open) return;
      const box = this.lightboxViewport.getBoundingClientRect();
      const center = box.left + box.width / 2;
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;
      this.slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const delta = Math.abs(rect.left + rect.width / 2 - center);
        if (delta < distance) {
          distance = delta;
          nearest = index;
        }
      });
      this.activate(nearest, false);
      this.queueMount(nearest);
    });
  }

  activate(index, mountMedia = true) {
    const total = this.slides.length;
    if (!total) return;
    const next = (index + total) % total;
    this.slides.forEach((slide, i) => slide.classList.toggle('active', i === next));

    // The title itself now lives below the video, so the status line stays a
    // counter and the copy is not duplicated.
    if (this.status) this.status.textContent = `Video ${next + 1} of ${total}`;

    if (mountMedia) {
      this.clearPendingMount();
      this.mountActive(next);
    } else if (this.mountedIndex !== next) {
      this.pauseMounted();
    }
  }

  queueMount(index) {
    this.clearPendingMount();
    this.mountTimer = window.setTimeout(() => {
      this.mountTimer = 0;
      if (!this.dialog?.open) return;
      this.mountActive(index);
    }, 120);
  }

  clearPendingMount() {
    if (!this.mountTimer) return;
    window.clearTimeout(this.mountTimer);
    this.mountTimer = 0;
  }

  mountActive(index) {
    if (this.mountedIndex === index) return;
    this.unmount();
    this.mount(this.slides[index]);
    this.mountedIndex = index;
  }

  mount(slide) {
    const player = slide.querySelector('[data-ryoxzel-inspiration-player]');
    const source = slide.querySelector('[data-ryoxzel-inspiration-source]');
    if (!player || !source?.content?.childElementCount) return;
    player.replaceChildren(source.content.cloneNode(true));
    const media = player.querySelector('deferred-media');
    media?.loadContent?.(false);
    media?.playMedia?.();
  }

  pauseMounted() {
    if (this.mountedIndex == null) return;
    this.slides[this.mountedIndex]
      ?.querySelector('deferred-media')
      ?.pauseMedia?.();
  }

  /** Pauses the mounted video and empties the player so the poster shows again. */
  unmount() {
    this.clearPendingMount();
    if (this.mountedIndex == null) return;
    const player = this.slides[this.mountedIndex]?.querySelector('[data-ryoxzel-inspiration-player]');
    this.pauseMounted();
    player?.replaceChildren();
    this.mountedIndex = null;
  }

  destroy() {
    this.unmount();
    cancelAnimationFrame(this.frame);
    this.abortController.abort();
  }
}

function findShelves(root) {
  if (!(root instanceof HTMLElement || root instanceof Document)) return [];
  const found = [...root.querySelectorAll('[data-ryoxzel-inspiration-shelf]')];
  if (root instanceof HTMLElement && root.matches('[data-ryoxzel-inspiration-shelf]')) found.unshift(root);
  return found;
}

function initShelf(section) {
  if (!(section instanceof HTMLElement) || shelves.has(section)) return;
  shelves.set(section, new RyoxzelInspirationShelf(section));
}

function destroyShelf(section) {
  shelves.get(section)?.destroy();
  shelves.delete(section);
}

function initDocumentShelves() {
  findShelves(document).forEach(initShelf);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDocumentShelves, { once: true });
} else {
  initDocumentShelves();
}

document.addEventListener('shopify:section:load', (event) => {
  findShelves(event.target).forEach(initShelf);
});

document.addEventListener('shopify:section:unload', (event) => {
  findShelves(event.target).forEach(destroyShelf);
});
